import React, { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, Check } from 'lucide-react';
import { useLanguage } from './LanguageContext';

/**
 * Language switcher component
 * Displays a dropdown to select the current language
 */
const LanguageSwitcher: React.FC = () => {
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Handle language selection
   */
  const handleLanguageSelect = async (langCode: string) => {
    await changeLanguage(langCode as any);
    setIsOpen(false);
  };

  /**
   * Get current language info
   */
  const currentLang = availableLanguages.find(lang => lang.code === currentLanguage);

  return (
    <div style={containerStyle} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={buttonStyle}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span style={flagStyle}>{currentLang?.flag}</span>
        <span style={codeStyle}>{currentLang?.code.toUpperCase()}</span>
        <span style={arrowStyle}>{isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
      </button>

      {isOpen && (
        <div style={dropdownStyle}>
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageSelect(lang.code)}
              style={{
                ...dropdownItemStyle,
                ...(lang.code === currentLanguage ? activeItemStyle : {}),
              }}
              aria-label={`Switch to ${lang.name}`}
            >
              <span style={flagStyle}>{lang.flag}</span>
              <span style={nameStyle}>{lang.name}</span>
              {lang.code === currentLanguage && (
                <span style={checkmarkStyle}><Check size={14} /></span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const buttonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  backgroundColor: 'white',
  border: '1px solid #ddd',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const flagStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  lineHeight: 1,
};

const codeStyle: React.CSSProperties = {
  textTransform: 'uppercase',
  fontWeight: '600',
  color: '#333',
};

const arrowStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: '#666',
  marginLeft: '0.25rem',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 0.5rem)',
  right: 0,
  backgroundColor: 'white',
  border: '1px solid #ddd',
  borderRadius: '6px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  minWidth: '180px',
  zIndex: 1000,
  overflow: 'hidden',
};

const dropdownItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  width: '100%',
  padding: '0.75rem 1rem',
  backgroundColor: 'white',
  border: 'none',
  borderBottom: '1px solid #f0f0f0',
  cursor: 'pointer',
  fontSize: '0.9rem',
  textAlign: 'left',
  transition: 'background-color 0.2s ease',
};

const activeItemStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  fontWeight: '600',
};

const nameStyle: React.CSSProperties = {
  flex: 1,
  color: '#333',
};

const checkmarkStyle: React.CSSProperties = {
  color: '#28a745',
  fontWeight: 'bold',
  fontSize: '1rem',
};

export default LanguageSwitcher;

